import React, { useMemo, useRef, useState } from 'react';

import { TeamMemberList } from '@/components//TeamMemberList';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { TeamMemberCard } from '@/components/TeamMemberCard';
import { ProfileModal } from '@/components/ProfileModal';
import {
	Invite,
	useDeleteInviteMutation,
	useResendInviteMutation,
} from '@/graphql';
import { sentenceCase } from '@/helpers/changeCase';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { TeamMembership, useTeam } from '@/providers/TeamProvider';
import { useSnackbar } from 'notistack';

import { TeamMemberLayout } from './TeamMemberLayout';
import { TeamName } from './TeamName';

export interface TeamMembersProps {
	onInviteClick: () => void;
	onEditTeamNameClick?: () => void;
	onClose?: () => void;
}

export const TeamMembers = ({
	onInviteClick,
	onEditTeamNameClick,
	onClose,
}: TeamMembersProps) => {
	const { isLoading: isTeamLoading, team, refetch } = useTeam();
	const { user } = useAuth();
	const { enqueueSnackbar } = useSnackbar();
	const { mutateAsync: deleteInvite } = useDeleteInviteMutation();
	const { mutateAsync: resendInvite } = useResendInviteMutation();
	const fireResendInviteEvent = useAnalyticsEvent(
		'Team_Members_Resend_Invite',
	);
	const fireDeleteInviteEvent = useAnalyticsEvent(
		'Team_Members_Delete_Invite',
	);

	const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
	const lastSelectedMemberIdRef = useRef<string | null>(null);
	if (selectedMemberId !== null) lastSelectedMemberIdRef.current = selectedMemberId;
	const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [activeSkill, setActiveSkill] = useState<string | null>(null);

	const filteredMembers = useMemo(() => {
		return [...(team.members ?? [])]
			.sort((a, b) => {
				if (a?.user.id === user.id) return -1;
				if (b?.user.id === user.id) return 1;
				return 0;
			})
			.filter((m) => {
				if (!m) return false;
				const name = (m.user.displayName || m.user.email).toLowerCase();
				const nameMatch = !searchQuery || name.includes(searchQuery.toLowerCase());
				const skillMatch =
					!activeSkill ||
					(activeSkill === 'admin'
						? m.roles.includes('teamAdmin')
						: (m.user.skills ?? []).includes(activeSkill));
				return nameMatch && skillMatch;
			});
	}, [team.members, user.id, searchQuery, activeSkill]);

	const uniqueSkills = useMemo(() => {
		const skillSet = new Set<string>();
		(team.members ?? []).forEach((m) => {
			(m?.user.skills ?? []).forEach((s) => skillSet.add(s));
		});
		return Array.from(skillSet).sort();
	}, [team.members]);

	// const navigateToDm = useCallback(
	// 	(userId: string) => {
	// 		if (!isNil(onClose)) {
	// 			onClose();
	// 		}
	// 		if (chatChannels.hasOwnProperty(userId)) {
	// 			navigate(
	// 				directMessageUrl.replace(':token', chatChannels[userId]),
	// 				{
	// 					state: {
	// 						linkFrom: LinkFrom.messages,
	// 					},
	// 				},
	// 			);
	// 		} else {
	// 			createChatChannel(
	// 				userId,
	// 				profile?.userId!,
	// 				enqueueSnackbar,
	// 				navigate,
	// 				'messages',
	// 			);
	// 		}
	// 	},
	// 	[onClose, chatChannels, navigate, profile?.userId, enqueueSnackbar],
	// );

	const mapMembershipToTeamMember = (item: TeamMembership | null) => {
		if (!item) {
			return null;
		}
		const isAdmin = item.roles.includes('teamAdmin');
		const isSelf = user.id === item.user.id;
		// const lastActive = item.lastActive;

		return (
			<TeamMemberCard
				key={item.user.id}
				id={item.user.id}
				name={item.user.displayName || item.user.email}
				email={item.user.email}
				role={item.user.skills?.map((s) => sentenceCase(s)).join(', ') || null}
				isSelf={isSelf}
				isAdmin={isAdmin}
				lastActive={null}
				invitePending={false}
				actions={[]}
				onRowPress={() => setSelectedMemberId(item.user.id)}
				// profilePhoto={item.profilePhoto}
			/>
		);
	};

	const mapInviteToTeamMember = (item: Invite | null) => {
		if (!item) {
			return null;
		}

		return (
			<TeamMemberCard
				key={item.id}
				id={item.id}
				name={item.email}
				email={item.email}
				isSelf={false}
				isAdmin={false}
				lastActive={null}
				invitePending={true}
				actions={[]}
				onRowPress={() => setSelectedInvite(item)}
				// profilePhoto={item.profilePhoto}
			/>
		);
	};

	const handleResendInvite = async () => {
		if (!selectedInvite) return;
		try {
			await resendInvite({ id: selectedInvite.id });
			fireResendInviteEvent();
			enqueueSnackbar('Invite resent');
		} catch {
			enqueueSnackbar('Something went wrong with resending this invite');
		}
		setSelectedInvite(null);
	};

	const handleDeleteInvite = async () => {
		if (!selectedInvite) return;
		try {
			await deleteInvite({ id: selectedInvite.id });
			fireDeleteInviteEvent();
			enqueueSnackbar('Invite deleted');
			refetch();
		} catch {
			enqueueSnackbar('Something went wrong with deleting this invite');
		}
		setSelectedInvite(null);
	};

	const adminCount = (team?.members ?? []).filter((m) => m?.roles.includes('teamAdmin')).length;

	return (
		<>
			<TeamMemberLayout
				membersList={
					<TeamMemberList loading={isTeamLoading} hideHeader>
						{filteredMembers.map(mapMembershipToTeamMember)}
						{team.invites?.map(mapInviteToTeamMember)}
					</TeamMemberList>
				}
				onInviteClick={onInviteClick}
				teamInfoLoading={isTeamLoading}
				searchQuery={searchQuery}
				onSearchChange={(q: string) => setSearchQuery(q)}
				activeSkill={activeSkill}
				onSkillChange={(s: string | null) => setActiveSkill(s)}
				uniqueSkills={uniqueSkills}
				adminCount={adminCount}
			/>
			<ProfileModal
				memberId={selectedMemberId ?? lastSelectedMemberIdRef.current ?? ''}
				open={selectedMemberId !== null}
				onClose={() => setSelectedMemberId(null)}
			/>
			<ActionSheet
				open={selectedInvite !== null}
				title={selectedInvite?.email ?? ''}
				onClose={() => setSelectedInvite(null)}
			>
				<ActionSheetAction
					label="Resend invite"
					onActionClick={handleResendInvite}
				/>
				<ActionSheetAction
					label="Delete invite"
					type="danger"
					onActionClick={handleDeleteInvite}
				/>
			</ActionSheet>
		</>
	);
};
